<template>
  <div class="view-team-join">
    <template v-if="team">
      <template v-if="membership?.accepted">
        {{ $t("messages.alreadyJoined") }}
      </template>
      <template v-else>
        <h1>
          {{ $t("headings.teamInvitation", { name: team.name }) }}
        </h1>

        <i18n-t tag="p" keypath="texts.invitedToTeam" scope="global">
          <template #teamname>
            <strong>{{ team.name }}</strong>
          </template>
          <template #organizationname>
            <strong>{{ team.organizationName }}</strong>
          </template>
        </i18n-t>
        <p>
          {{ $t("texts.teamJoiningDetails") }}
        </p>

        <div class="line_flex -center">
          <button class="button" type="button" @click="accept()">
            {{ $t("actions.joinTeam") }}
          </button>

          <button
            class="button button_secondary"
            type="button"
            @click="decline()"
          >
            {{ $t("actions.rejectInvitation") }}
          </button>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { useOrganizations } from "@manager/organizations/organizations.store";
import { useUser } from "@/api/user.store";
import { useWorkspaces } from "@/api/workspaces.store";
import { showError } from "@core/ui/alert/alert";

import type { OrganizationWorkspace } from "@/api/Workspace";

const props = defineProps<{
  organizationSlug: string;
  teamSlug: string;
}>();

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { user } = useUser();

const { getTeam } = useOrganizations(props, { user });

const {
  slugWorkspace,
  leaveTeam,
  acceptTeamInvitation
} = useWorkspaces(props);

getTeam({ slug: props.organizationSlug }, props.teamSlug).then(
  (team) => {
    if (team.membership?.accepted) {
      router.push({
        name: "team",
        params: {
          organizationSlug: props.organizationSlug,
          teamSlug: props.teamSlug
        }
      });
    }
  }
);

const team = computed(() => slugWorkspace.value as OrganizationWorkspace | null);

const membership = computed(() => {
  return team.value?.membership;
});

const redirectPath = computed(() => {
  if (route.redirectedFrom) {
    return {
      path: route.redirectedFrom.path,
      params: route.redirectedFrom.params
    };
  }
  return {
    name: "team",
    params: {
      organizationSlug: props.organizationSlug,
      teamSlug: props.teamSlug
    }
  };
});

async function accept() {
  if (!team.value) {
    showError(t("errors.teamNotFound"));
    return;
  }

  if (!user.value) {
    return;
  }

  try {
    await acceptTeamInvitation(team.value, user.value);
    router.replace(redirectPath.value);
  } catch (error) {
    showError(error as Error);
  }
}

async function decline() {
  if (!team.value) {
    showError(t("errors.teamNotFound"));
    return;
  }

  try {
    await leaveTeam(team.value);
    router.push({
      name: "models-explorer"
    });
  } catch (error) {
    console.log(error);
  }
}
</script>

<style lang="scss">
.view-team-join {
  display: block;
  overflow: auto;
  margin: auto;
  max-height: 100%;
  max-width: 40rem;
  text-align: center;
  padding: 2rem 3rem;

  h1 {
    margin-top: 0;
    hyphens: auto;
    overflow-wrap: break-word;
  }
  p {
    hyphens: auto;
    overflow-wrap: break-word;
  }
}
</style>
